import React, { ErrorInfo } from "react";
import { useTranslation } from "react-i18next";

//#region lazy

import { getCurrentCulture, redirectToHomePage, resetSession } from "@utils";

import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { Button } from "@components/buttons";
import { useEffect } from "react";
import { useRootAppStore } from "@hooks";
import { redirect } from "@state";
import { ErrorBoundaryHoc } from "@components/boundary/ErrorBoundaryHoc";
import { logger } from "@utils/logger/logger";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const UserMenu = lazy(loadFailPath, () => import("../user-menu/UserMenu"));

//#endregion lazy

export default ErrorBoundaryHoc(ErrorPageContent);

function ErrorPageContent(props: {
	reload: () => void;
	full?: boolean;
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen: () => void;
	error: string;
	errorInfo: ErrorInfo | string;
}) {
	const { full, isNavMenuOpen, setIsNavMenuOpen, error } = props;
	const rootStore = useRootAppStore();

	useEffect(() => {
		logger.logError(error, {
			level: "fatal",
			extra: { isErrorBoundary: true },
		});

		if (WEBPACK_IS_SUPPORT_CHAT_AVAILABLE) {
			const { message, stack } = new Error(error);
			rootStore.diagnosticsProvider.recordErrorMsg({
				errData: {
					message,
					stack,
				},
				timestamp: new Date().toISOString(),
			});
		}
	}, []);

	if (full) {
		return (
			<FullErrorPage
				isNavMenuOpen={isNavMenuOpen}
				setIsNavMenuOpen={setIsNavMenuOpen}
			/>
		);
	}

	return (
		<ErrorPageBody
			isNavMenuOpen={isNavMenuOpen}
			setIsNavMenuOpen={setIsNavMenuOpen}
		/>
	);
}

function FullErrorPage(props: {
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen: () => void;
}) {
	const { t } = useTranslation();
	const { isNavMenuOpen, setIsNavMenuOpen } = props;

	return (
		<>
			<div className="container container--base" data-at="at-ErrorPage">
				<div className="card">
					<div className="card__body">
						<i className="u-icon u-icon--huge u-icon--empty u-icon--alert-circle u-color--sent--danger" />
						<div className="card__title card__title--center u-type--big">
							{t("ERROR.TITLE")}
						</div>
					</div>
					<div className="card__body">
						<div>{t("ERROR.MESSAGE")}</div>
						<div>{t("ERROR.MESSAGE_DETAILS")}</div>
					</div>
					<div className="card__footer">
						<Button
							type="button"
							atClassName="errorPageFullBtn"
							className="btn--base btn--block btn--primary--inverted card__footer__btn"
							iconLeft
							iconClassName="u-icon--sml u-icon--nav--home"
							btnText={t("ERROR.404.BACK_TO_HOME")}
							onClick={redirectToHomePage}
						/>
						{WEBPACK_IS_SUPPORT_CHAT_AVAILABLE && (
							<Button
								type="button"
								atClassName="at-ErrorPage-SupportLink"
								className="btn--wrap btn--wrap--base btn--block btn--primary--inverted btn--icon card__footer__btn"
								btnContent={
									<>
										<i className="u-icon u-icon--sml u-icon--support--alt" />
										<span className="btn__label u-type--center">
											{t("CHAT.SUPPORT.TITLE")}
										</span>
									</>
								}
								onClick={() => {
									redirect(
										`/${getCurrentCulture()}/support-chat`
									);
								}}
							/>
						)}
					</div>

					<div className="card__footer">
						<div>{t("ERROR.404.RESET_SESSION_SECTION")}</div>
						<button
							type="button"
							onClick={resetSession}
							className="card__footer__btn card__btn--primary btn btn--base btn--block btn--accent btn--icon"
							data-at="at-ErrorPage__btn--primary"
						>
							<i className="u-icon u-icon--sml u-icon--refresh u-color--accent--text" />
							<span>{t("ERROR.RESET_SESSION_LABEL")}</span>
						</button>
					</div>
				</div>
			</div>
			{isNavMenuOpen && (
				<UserMenu
					isNavMenuOpen={isNavMenuOpen}
					setIsNavMenuOpen={setIsNavMenuOpen}
				/>
			)}
		</>
	);
}

function ErrorPageBody(props: {
	isNavMenuOpen?: boolean;
	setIsNavMenuOpen: () => void;
}) {
	const { t } = useTranslation();
	const { isNavMenuOpen, setIsNavMenuOpen } = props;
	return (
		<>
			<div className="container container--base">
				<div className="card">
					<div className="card__body u-type--center">
						<i className="u-icon u-icon--huge u-icon--empty u-icon--alert-circle u-color--sent--danger" />
						<div className="card__title u-type--big">
							{t("ERROR.TITLE")}
						</div>
					</div>
					<div className="card__body u-type--center">
						<div>{t("ERROR.MESSAGE")}</div>
						<div>{t("ERROR.MESSAGE_DETAILS")}</div>
					</div>
					<div className="card__footer">
						<Button
							className="btn--base btn--block btn--primary--inverted btn--icon"
							atClassName="errorPageFullBtn"
							type="button"
							onClick={redirectToHomePage}
							btnContent={
								<>
									<i className="u-icon u-icon--sml u-icon--nav--home" />
									<span>{t("ERROR.404.BACK_TO_HOME")}</span>
								</>
							}
						/>
						<div className="u-mar--bottom--xsml">
							{t("ERROR.404.RESET_SESSION_SECTION")}
						</div>
						<button
							type="button"
							className="at-ErrorCta card__footer__btn btn btn--base btn--block btn--accent"
							onClick={resetSession}
						>
							{t("ERROR.RESET_SESSION_LABEL")}
						</button>
					</div>
				</div>
			</div>
			{isNavMenuOpen && (
				<UserMenu
					isNavMenuOpen={isNavMenuOpen}
					setIsNavMenuOpen={setIsNavMenuOpen}
				/>
			)}
		</>
	);
}
